/**
 * Tabletop link
 *
 * Lets a box or a team live on a Tabletop character sheet
 * (tabletop.wamsdndhub.com, "Pokemon: Pen & Paper") instead of in this
 * browser. A character has exactly one box, holding every Pokemon they own,
 * and any number of parties; a party's slots are the box's own Pokemon, so an
 * edit made in either place is the same Pokemon edited.
 *
 * Linked teams are loaded from Tabletop when the player logs in, never written
 * to localStorage (PSTeams#save filters them out), and every save of one is
 * pushed back through the Tabletop bridge API, authenticated with the same
 * Supabase session this client already logs in with. Each Pokemon, box and
 * party carries a version; a push naming an older one is refused and the
 * linked teams reload, rather than silently overwriting a newer edit.
 *
 * Storage > Tabletop character sheet (panel-teambuilder-team.tsx) is where a
 * player links or unlinks one.
 *
 * The same bridge carries the campaign's tournaments (Your match on the home
 * screen, the Tournaments page, and each result reported back when a
 * "Tabletop Match" battle ends), a GM's Save to record on a finished battle,
 * and the replays MyBattles! on the Rotom Phone links to (?tabletop_replay=).
 */

import { PS, type RoomID, type Team } from './client-main';
import { Teams } from './battle-teams';
import { toID } from './battle-dex';
import { BattleLog } from './battle-log';
import type { Battle, Side } from './battle';

interface BridgeSet { mon_id: number; version: number; set: Teams.PokemonSet; items?: string[] }
interface BridgeParty { id: number; name: string; format: string; slots: number[]; version: number; sets: BridgeSet[] }
interface BridgeTeams {
	character: { id: number, name: string, campaign: string };
	box: { id: number, name: string, format: string, version: number, sets: BridgeSet[] } | null;
	parties: BridgeParty[];
}
export interface TabletopCharacter {
	id: number;
	name: string;
	campaign: string;
	archived: boolean;
	has_box: boolean;
	box_name: string;
	parties: { id: number, name: string }[];
	/** Fingerprint of the character's box and parties; moves when anything changes on the sheet. */
	stamp?: string;
	/** A GM's NPC trainer rather than the player's own character. */
	npc?: boolean;
	/** The sheet's Customize colors (sheet.ui.dnd_theme), for Options > Colors > Match. */
	theme?: { preset?: string, colors?: { [key: string]: string }, name?: string } | null;
}

/** A match waiting on this user (or on one of their NPC trainers, for a GM). */
export interface TabletopMatch {
	match_id: number;
	tournament: string;
	campaign: string;
	label: string;
	me: { id: number, name: string, npc: boolean };
	opponent: { id: number, name: string, npc: boolean, showdown: string };
}
export interface TabletopBracketMatch {
	code: string;
	bracket: 'W' | 'L' | 'F';
	round: number;
	label: string;
	a: number | null;
	b: number | null;
	state: 'waiting' | 'ready' | 'done' | 'bye' | 'void';
	winner: number | null;
	id: number | null;
	source: string;
}
export interface TabletopTournament {
	id: number;
	name: string;
	format: 'single' | 'double';
	status: 'running' | 'complete';
	note: string;
	champion_id: number | null;
	campaign: string;
	entries: { id: number, seed: number, name: string, npc: boolean, showdown: string }[];
	matches: TabletopBracketMatch[];
}
/** A campaign this user runs, for Save to record. */
export interface TabletopGmCampaign {
	id: number;
	name: string;
	characters: { id: number, name: string, npc: boolean, showdown: string }[];
}

const FORMAT = 'gen9campaignprimary';
/** How often an open, visible client checks Tabletop for box changes. */
const SYNC_EVERY = 12000;

export const PSTabletop = new class {
	characters: TabletopCharacter[] | null = null;
	error = '';
	loading: Promise<void> | null = null;
	pushTimer: ReturnType<typeof setTimeout> | null = null;
	pushing: Promise<void> | null = null;
	deepLink: { character: number, mon: number, link: string } | null = null;
	/** Each linked character's stamp as of the teams on screen. */
	stamps = new Map<number, string>();
	syncing = false;
	lastSync = 0;
	/** Characters this client just saved to, whose next stamp change is our own. */
	pushed = new Set<number>();
	tournaments: TabletopTournament[] | null = null;
	myMatches: TabletopMatch[] = [];
	lastTournaments = 0;
	loadingTournaments = false;
	gmCampaigns: TabletopGmCampaign[] | null = null;
	/** Battles already reported to their tournament, so a battle reports once. */
	reported = new Set<string>();
	replayLink = 0;

	constructor() {
		try {
			const params = new URLSearchParams(location.search);
			this.replayLink = Number(params.get('tabletop_replay')) || 0;
			const character = Number(params.get('tabletop_character')) || 0;
			if (character) {
				this.deepLink = {
					character,
					mon: Number(params.get('tabletop_mon')) || 0,
					link: params.get('tabletop_link') || '',
				};
			}
		} catch {}
		// Live sync: a box edited on the character sheet (or by a trade, or the
		// GM) shows up here on its own, with no refresh. Checks are cheap (one
		// small list) and only a character whose stamp moved is reloaded.
		setInterval(() => void this.sync(), SYNC_EVERY);
		document.addEventListener('visibilitychange', () => {
			if (!document.hidden) void this.sync();
		});
		window.addEventListener('focus', () => void this.sync());
	}

	/** Pick up changes made on Tabletop since the teams were loaded. */
	async sync() {
		if (!this.characters || this.loading || this.pushing || this.pushTimer || this.syncing) return;
		if (document.hidden || Date.now() - this.lastSync < 4000) return;
		this.syncing = true;
		this.lastSync = Date.now();
		try {
			const data = await this.request<{ characters: TabletopCharacter[] }>('GET', 'characters');
			const characters = data.characters || [];
			const known = new Set(characters.map(c => c.id));
			let changed = characters.length !== this.characters.length;
			for (const character of characters) {
				if (await this.syncCharacter(character)) changed = true;
			}
			for (const team of [...PS.teams.list]) {
				if (team.tabletop && !known.has(team.tabletop.characterId)) {
					this.removeTeam(team);
					changed = true;
				}
			}
			this.characters = characters;
			this.error = '';
			if (changed) {
				PS.teams.update('team');
				PS.update();
			}
		} catch {
			// Offline or logged out: the next check tries again.
		} finally {
			this.syncing = false;
		}
	}

	/** One character of a sync: true when its teams on screen changed. */
	async syncCharacter(character: TabletopCharacter): Promise<boolean> {
		const id = character.id;
		const mine = PS.teams.list.filter(t => t.tabletop?.characterId === id);
		if (!character.has_box && !character.parties.length) {
			this.stamps.delete(id);
			if (!mine.length) return false;
			this.dropCharacter(id);
			return true;
		}
		if (this.stamps.get(id) === character.stamp) return false;
		// Never write over what the player is editing right now: their unsaved
		// changes are about to be pushed, and a field they are typing in would
		// lose its text. The next check picks it up.
		const dirty = mine.some(t => t.packedTeam !== t.tabletop!.savedPacked || t.name !== t.tabletop!.savedName);
		if (dirty || document.activeElement?.closest?.('.teameditor')) return false;
		// Our own save moved the stamp: note it, nothing to fetch (a reload
		// would only shuffle the box into the sheet's arrangement).
		if (this.pushed.has(id)) {
			this.pushed.delete(id);
			if (character.stamp) this.stamps.set(id, character.stamp);
			return false;
		}
		this.applyTeams(await this.request<BridgeTeams>('GET', `characters/${id}/teams`));
		if (character.stamp) this.stamps.set(id, character.stamp);
		return true;
	}

	/** Staging pkmn talks to staging Tabletop; Live to Live. */
	get apiBase() {
		const host = location.hostname;
		return /stage|localhost|127\.0\.0\.1/.test(host) ?
			'https://tabletop-stage.wamsdndhub.com' : 'https://tabletop.wamsdndhub.com';
	}

	get sheetBase() {
		return this.apiBase;
	}

	async request<T = any>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
		const token = await PS.user.getSupabaseAccessToken();
		if (!token) throw new Error('Log in to link teams to a Tabletop character sheet.');
		const response = await fetch(`${this.apiBase}/api/pokemon/bridge/${path}`, {
			method,
			headers: {
				'Accept': 'application/json',
				'Authorization': `Bearer ${token}`,
				...(body ? { 'Content-Type': 'application/json' } : {}),
			},
			body: body ? JSON.stringify(body) : undefined,
		});
		let data: any = null;
		try {
			data = await response.json();
		} catch {}
		if (!response.ok || !data || data.ok === false) {
			const error = new Error(data?.message || `Tabletop answered ${response.status}.`) as Error & { conflict?: boolean };
			error.conflict = response.status === 409 || !!data?.conflict;
			throw error;
		}
		return data as T;
	}

	onLogin() {
		void this.load();
		void this.loadTournaments(true);
		void this.loadGmCampaigns();
		if (this.replayLink) {
			const id = this.replayLink;
			this.replayLink = 0;
			void this.openReplay(id);
		}
	}

	onLogout() {
		this.characters = null;
		this.tournaments = null;
		this.myMatches = [];
		this.gmCampaigns = null;
		for (const team of [...PS.teams.list]) {
			if (team.tabletop) this.removeTeam(team);
		}
		PS.teams.update('team');
	}

	/** Fetch the player's Pokemon characters, and every linked box and party. */
	load(): Promise<void> {
		if (this.loading) return this.loading;
		this.loading = (async () => {
			try {
				const data = await this.request<{ characters: TabletopCharacter[] }>('GET', 'characters');
				this.characters = data.characters || [];
				this.error = '';
				const seen = new Set<number>();
				for (const character of this.characters) {
					seen.add(character.id);
					if (!character.has_box && !character.parties.length) {
						this.dropCharacter(character.id);
						continue;
					}
					this.applyTeams(await this.request<BridgeTeams>('GET', `characters/${character.id}/teams`));
					if (character.stamp) this.stamps.set(character.id, character.stamp);
				}
				for (const team of [...PS.teams.list]) {
					if (team.tabletop && !seen.has(team.tabletop.characterId)) this.removeTeam(team);
				}
				PS.teams.update('team');
				this.handleDeepLink();
			} catch (err: any) {
				this.error = err?.message || 'Could not reach Tabletop.';
			} finally {
				this.loading = null;
			}
		})();
		return this.loading;
	}

	/** The characters list alone, for the Storage panel. */
	async refreshCharacters() {
		try {
			const data = await this.request<{ characters: TabletopCharacter[] }>('GET', 'characters');
			this.characters = data.characters || [];
			this.error = '';
		} catch (err: any) {
			this.error = err?.message || 'Could not reach Tabletop.';
		}
		PS.teams.update('team');
		PS.update();
	}

	// ------------------------------------------------------------ tournaments

	/** The campaign's tournaments and this user's waiting matches (every 15s at most). */
	async loadTournaments(force = false) {
		if (!PS.user.named || this.loadingTournaments) return;
		if (!force && Date.now() - this.lastTournaments < 15000) return;
		this.loadingTournaments = true;
		this.lastTournaments = Date.now();
		try {
			const data = await this.request<{ tournaments: TabletopTournament[], my_matches: TabletopMatch[] }>('GET', 'tournaments');
			this.tournaments = data.tournaments || [];
			this.myMatches = data.my_matches || [];
			PS.update();
		} catch {
			// Not linked to Tabletop, or offline: no tournaments to show.
		} finally {
			this.loadingTournaments = false;
		}
	}

	/** Open the challenge to a match's opponent, carrying the match. */
	challengeMatch(matchId: number) {
		const match = this.myMatches.find(m => m.match_id === matchId);
		if (!match) return;
		PS.join(`challenge-${toID(match.opponent.showdown)}` as RoomID, {
			args: { format: `${FORMAT}@@@Tabletop Match = ${match.match_id}` },
		});
	}

	/** The tournament match a battle is, from the rule it announced at the start. */
	matchOf(battle: Battle | null) {
		const line = battle?.stepQueue.find(l => l.startsWith('|rule|Tabletop Match: '));
		return line ? Number(line.slice(22)) || 0 : 0;
	}

	/** A finished battle, as Tabletop stores it: both sides as the battle showed them. */
	battleRecord(roomid: string, battle: Battle) {
		const end = [...battle.stepQueue].reverse().find(l => l.startsWith('|win|') || l.startsWith('|tie'));
		let winner = '';
		if (end?.startsWith('|win|')) {
			const name = toID(end.slice(5));
			winner = name === toID(battle.p1.name) ? 'p1' : name === toID(battle.p2.name) ? 'p2' : '';
		}
		const team = (side: Side) => side.pokemon.slice(0, 6).map(p => ({
			species: p.speciesForme.replace(/-\*$/, ''),
			name: p.name !== p.speciesForme ? p.name : '',
			level: p.level,
			item: p.item || '',
			ability: p.ability || '',
			moves: p.moveTrack.map(([move]) => move).filter(move => !move.startsWith('*')),
			shiny: p.shiny,
			fainted: p.fainted,
			hp: p.maxhp ? Math.round(p.hp / p.maxhp * 100) : 0,
		}));
		return {
			battle_id: roomid.replace(/^battle-/, ''),
			format: battle.tier,
			title: `${battle.p1.name} vs. ${battle.p2.name}`,
			p1: { name: battle.p1.name },
			p2: { name: battle.p2.name },
			winner,
			turns: battle.turn,
			teams: { p1: team(battle.p1), p2: team(battle.p2) },
			log: battle.stepQueue.join('\n'),
		};
	}

	/**
	 * A battle just ended. If it was a tournament match and we played in it,
	 * send the result to the bracket (the other player's client does too;
	 * Tabletop only counts it once) and say what happened in the battle log.
	 */
	battleEnded(roomid: string, battle: Battle, playing: boolean) {
		const matchId = this.matchOf(battle);
		if (!matchId || !playing || this.reported.has(roomid) || !PS.user.named) return;
		this.reported.add(roomid);
		const say = (html: string) => battle.instantAdd(`|raw|<div class="broadcast-blue tabletop-match-note">${html}</div>`);
		this.request<{ message: string }>('POST', 'tournament-result', { match_id: matchId, ...this.battleRecord(roomid, battle) })
			.then(data => {
				say(`<strong>Tournament:</strong> ${BattleLog.escapeHTML(data.message || 'Result sent.')} It's on both trainers' records too.`);
				void this.loadTournaments(true);
			})
			.catch(err => {
				this.reported.delete(roomid);
				say(`<strong>Tournament:</strong> ${BattleLog.escapeHTML(err?.message || 'Could not reach Tabletop.')}`);
			});
	}

	// --------------------------------------------------------- battle records

	/** The campaigns this user runs (empty for players): Save to record shows for GMs only. */
	async loadGmCampaigns() {
		try {
			const data = await this.request<{ campaigns: TabletopGmCampaign[] }>('GET', 'gm-campaigns');
			this.gmCampaigns = data.campaigns || [];
		} catch {
			this.gmCampaigns = [];
		}
		PS.update();
	}

	async saveRecord(roomid: string, battle: Battle, choice: { template_id: number, p1: number, p2: number, title: string }) {
		const record = this.battleRecord(roomid, battle);
		const matchId = this.matchOf(battle);
		return this.request<{ message: string }>('POST', 'battles', {
			...record,
			template_id: choice.template_id,
			title: choice.title || record.title,
			p1: { ...record.p1, character_id: choice.p1 || null },
			p2: { ...record.p2, character_id: choice.p2 || null },
			...(matchId ? { match_id: matchId } : {}),
		});
	}

	/** A saved battle from MyBattles!, played back in its own battle room. */
	async openReplay(id: number) {
		try {
			const data = await this.request<{ battle: { title: string, p1: string, p2: string, log: string } }>('GET', `battles/${id}`);
			const roomid = `battle-tabletop-${id}` as RoomID;
			// Right after the page loads, the server's answer to joining this
			// room (it has none) can land after the log and reset the battle,
			// so check again a little later and play it in once more if so.
			const fill = () => {
				if (!PS.rooms[roomid]) PS.join(roomid);
				const room = PS.rooms[roomid] as any;
				if (!room) return;
				room.title = data.battle.title || `${data.battle.p1} vs. ${data.battle.p2}`;
				room.connectMode = null;
				if (!room.battle?.stepQueue?.length) PS.receive(`>${roomid}\n${data.battle.log}`);
			};
			fill();
			PS.focusRoom(roomid);
			setTimeout(fill, 1500);
			setTimeout(fill, 4000);
		} catch (err: any) {
			PS.alert(err?.message || 'That replay could not be loaded.');
		}
	}

	packSets(sets: BridgeSet[]) {
		return Teams.pack(sets.map(entry => entry.set));
	}

	/**
	 * Put one character's linked box and parties into the teambuilder. Teams
	 * already showing are updated in place, so an open editor keeps its room.
	 * `keep` is for the answer to our own push: the sets we just sent are what
	 * the player has on screen, so only the bookkeeping is refreshed, lined up
	 * with the on-screen order (Tabletop keeps its own box arrangement, so its
	 * answer can come back in a different order).
	 */
	applyTeams(data: BridgeTeams, keep?: { team: Team, sent: { mon_id: number, set: Teams.PokemonSet }[] }) {
		const characterId = data.character.id;
		const folder = `Tabletop: ${data.character.name}`;
		const wanted: Team[] = [];
		const build = (kind: 'box' | 'party', source: BridgeTeams['box'] | BridgeParty) => {
			if (!source) return;
			const partyId = kind === 'party' ? (source as BridgeParty).id : undefined;
			let team = PS.teams.list.find(t => t.tabletop?.characterId === characterId &&
				t.tabletop.kind === kind && t.tabletop.partyId === partyId);
			const packed = this.packSets(source.sets);
			const link = {
				characterId,
				characterName: data.character.name,
				kind,
				partyId,
				version: source.version,
				mons: source.sets.map(entry => ({
					id: entry.mon_id, version: entry.version, species: entry.set.species || '', name: entry.set.name || '',
					items: entry.items || [],
				})),
				savedPacked: packed,
				savedName: source.name,
			};
			if (team && keep && team === keep.team) {
				const byId = new Map(source.sets.map(entry => [entry.mon_id, entry]));
				const sentIds = new Set(keep.sent.map(entry => entry.mon_id).filter(Boolean));
				const claimed = new Set<number>();
				const aligned = keep.sent.map(entry => {
					let match = entry.mon_id ? byId.get(entry.mon_id) : undefined;
					// A new set got its id on the server: find it among the ids we didn't send.
					match ||= source.sets.find(candidate => !sentIds.has(candidate.mon_id) && !claimed.has(candidate.mon_id) &&
						toID(candidate.set.species) === toID(entry.set.species) && (candidate.set.name || '') === (entry.set.name || ''));
					if (match) claimed.add(match.mon_id);
					return {
						id: match?.mon_id || 0, version: match?.version || 0,
						species: entry.set.species || '', name: entry.set.name || '',
						items: match?.items || [],
					};
				});
				team.tabletop = { ...link, mons: aligned, savedPacked: team.packedTeam, savedName: team.name };
			} else if (team) {
				team.packedTeam = packed;
				team.name = source.name;
				team.iconCache = null;
				team.folder = folder;
				team.tabletop = link;
			} else {
				team = {
					name: source.name,
					format: (toID(source.format) || FORMAT) as any,
					folder,
					packedTeam: packed,
					iconCache: null,
					key: '',
					isBox: kind === 'box',
					tabletop: link,
				};
				PS.teams.push(team);
			}
			wanted.push(team);
		};
		build('box', data.box);
		for (const party of data.parties) build('party', party);
		for (const team of [...PS.teams.list]) {
			if (team.tabletop?.characterId === characterId && !wanted.includes(team)) this.removeTeam(team);
		}
	}

	dropCharacter(characterId: number) {
		for (const team of [...PS.teams.list]) {
			if (team.tabletop?.characterId === characterId) this.removeTeam(team);
		}
	}

	removeTeam(team: Team) {
		const index = PS.teams.list.indexOf(team);
		if (index >= 0) PS.teams.list.splice(index, 1);
		delete PS.teams.byKey[team.key];
	}

	/** Called from PSTeams#save: push any linked team that changed, a moment after typing stops. */
	onTeamsSaved() {
		const dirty = PS.teams.list.some(team => team.tabletop &&
			(team.packedTeam !== team.tabletop.savedPacked || team.name !== team.tabletop.savedName));
		if (!dirty) return;
		if (this.pushTimer) clearTimeout(this.pushTimer);
		this.pushTimer = setTimeout(() => {
			this.pushTimer = null;
			void this.push();
		}, 900);
	}

	async push(): Promise<void> {
		if (this.pushing) {
			await this.pushing;
			return this.push();
		}
		this.pushing = (async () => {
			for (const team of PS.teams.list.filter(t => t.tabletop)) {
				const link = team.tabletop!;
				if (team.packedTeam === link.savedPacked && team.name === link.savedName) continue;
				const sets = this.matchSets(Teams.unpack(team.packedTeam), link.mons);
				try {
					const data = link.kind === 'box' ?
						await this.request<BridgeTeams>('POST', `characters/${link.characterId}/box`, {
							version: link.version, name: team.name, sets,
						}) :
						await this.request<BridgeTeams>('POST', `characters/${link.characterId}/parties/${link.partyId!}`, {
							version: link.version, name: team.name, sets,
						});
					this.applyTeams(data, { team, sent: sets });
					this.pushed.add(link.characterId);
				} catch (err: any) {
					if (err?.conflict) {
						PS.alert(`"${team.name}" was changed somewhere else (the character sheet, or another device). ` +
							`The latest version has been loaded.`);
						await this.load();
					} else {
						PS.alert(`Couldn't save "${team.name}" to ${link.characterName}'s character sheet: ${err?.message || err}`);
					}
					break;
				}
			}
			PS.teams.update('team');
		})();
		try {
			await this.pushing;
		} catch {}
		this.pushing = null;
	}

	/**
	 * Which Tabletop Pokemon each edited set is. Showdown sets carry no id,
	 * so identity is inferred: an exact species+nickname match first (so
	 * reordering keeps every Pokemon itself), then the same species at the
	 * same position, then the same species anywhere; if the count is unchanged
	 * a leftover set keeps its position's Pokemon (an evolution or a species
	 * correction, not a new catch). Anything unmatched is new.
	 */
	matchSets(sets: Teams.PokemonSet[], mons: TabletopTeamMon[]) {
		const used = new Set<number>();
		const out: { mon_id: number, version?: number, set: Teams.PokemonSet }[] = sets.map(set => ({ mon_id: 0, set }));
		const take = (index: number, mon: TabletopTeamMon | undefined) => {
			if (!mon || used.has(mon.id)) return false;
			used.add(mon.id);
			out[index] = { mon_id: mon.id, version: mon.version, set: sets[index] };
			return true;
		};
		const same = (a: string, b: string) => toID(a) === toID(b);
		sets.forEach((set, i) => {
			take(i, mons.find(m => !used.has(m.id) && same(m.species, set.species) && (m.name || '') === (set.name || '')));
		});
		sets.forEach((set, i) => {
			if (out[i].mon_id) return;
			const atPosition = mons[i];
			if (atPosition && same(atPosition.species, set.species)) take(i, atPosition);
		});
		sets.forEach((set, i) => {
			if (out[i].mon_id) return;
			take(i, mons.find(m => !used.has(m.id) && same(m.species, set.species)));
		});
		if (sets.length === mons.length) {
			sets.forEach((set, i) => {
				if (!out[i].mon_id) take(i, mons[i]);
			});
		}
		return out;
	}

	/** Move a local box or team onto a character sheet. */
	async link(team: Team, characterId: number) {
		const sets = Teams.unpack(team.packedTeam);
		const path = team.isBox ? 'link-box' : 'link-party';
		const data = await this.request<BridgeTeams>('POST', `characters/${characterId}/${path}`, {
			name: team.name, format: team.format, sets,
		});
		// It lives on Tabletop now: the local copy goes, the linked one arrives.
		this.removeTeam(team);
		this.applyTeams(data);
		PS.teams.save();
		await this.refreshCharacters();
		const linked = PS.teams.list.find(t => t.tabletop?.characterId === characterId &&
			t.tabletop.kind === (team.isBox ? 'box' : 'party') && t.name === team.name) ||
			PS.teams.list.find(t => t.tabletop?.characterId === characterId && t.tabletop.kind === 'box');
		return linked || null;
	}

	/**
	 * Bring a linked team back into this browser. Unlinking a party returns
	 * just that party (its Pokemon stay in the character's box); unlinking the
	 * box returns the box and every party, since a party can't exist without
	 * the box its slots point at. Tabletop keeps nothing either way.
	 */
	async unlink(team: Team) {
		const link = team.tabletop;
		if (!link) return null;
		const localCopy = (name: string, sets: BridgeSet[], isBox: boolean): Team => ({
			name, format: team.format, folder: '', packedTeam: this.packSets(sets), iconCache: null, key: '', isBox,
		});
		let firstLocal: Team | null = null;
		if (link.kind === 'party') {
			const data = await this.request<{ unlinked: BridgeParty }>('POST', `characters/${link.characterId}/parties/${link.partyId!}/unlink`);
			firstLocal = localCopy(data.unlinked.name, data.unlinked.sets, false);
			PS.teams.push(firstLocal);
			this.removeTeam(team);
		} else {
			const data = await this.request<{ unlinked: BridgeTeams }>('POST', `characters/${link.characterId}/unlink-box`);
			const teams = data.unlinked;
			if (teams.box) {
				firstLocal = localCopy(teams.box.name, teams.box.sets, true);
				PS.teams.push(firstLocal);
			}
			for (const party of teams.parties) PS.teams.push(localCopy(party.name, party.sets, false));
			this.dropCharacter(link.characterId);
		}
		PS.teams.save();
		await this.refreshCharacters();
		return firstLocal;
	}

	/** Arriving from a character sheet's "Edit battle set" or "Link a box" link. */
	handleDeepLink() {
		const deepLink = this.deepLink;
		if (!deepLink) return;
		this.deepLink = null;
		const character = this.characters?.find(c => c.id === deepLink.character);
		if (!character) {
			PS.alert(`That character sheet isn't one of yours, or you're logged in as someone else.`);
			return;
		}
		if (deepLink.mon) {
			const teams = PS.teams.list.filter(t => t.tabletop?.characterId === character.id);
			const team = teams.find(t => t.tabletop!.kind === 'box' && t.tabletop!.mons.some(m => m.id === deepLink.mon)) ||
				teams.find(t => t.tabletop!.mons.some(m => m.id === deepLink.mon));
			if (team) {
				PS.join(`team-${team.key}` as RoomID);
				return;
			}
		}
		if (deepLink.link || !character.has_box) {
			PS.join('teambuilder' as RoomID);
			PS.alert(`To link a box to ${character.name}: open one of your boxes, press the storage button ` +
				`(it says "Local"), and choose "Tabletop character sheet".`);
			return;
		}
		const box = PS.teams.list.find(t => t.tabletop?.characterId === character.id && t.tabletop.kind === 'box');
		PS.join((box ? `team-${box.key}` : 'teambuilder') as RoomID);
	}
};

type TabletopTeamMon = { id: number, version: number, species: string, name: string };

(window as any).PSTabletop = PSTabletop;
